import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { changeLanguageHandler } from "../../util/userLanguageUtils";
import { NavigationItem } from "../../constants/common/StyledGlobalConstants";
import { Accordion, AccordionItem, AccordionHeader, AccordionDetail } from "@vds/accordions";
import { isNonERPCountryAccount } from "../../util/nonERPCountryUtils";
import RedIcon from "../../assets/icons/Red-icon.svg";
import { suspendedFinancialStatuses, REDBALL_FEATURE_TOGGLE, DEVICE_BREAKPOINTS } from "../../constants/appConstants";
import CautionIcon from "../../assets/icons/cautionIcon.svg";
import { hasContractAccountDetails } from "../../util/util";
import PropTypes from "prop-types";

const { DESKTOP } = DEVICE_BREAKPOINTS;

const StyledAccordionSubItems = styled.div`
  padding: 14px 16px;
  font-family: neue-haas-grotesk-text;
  font-size: 14px;
  font-style: normal;
  line-height: 20px;
  letter-spacing: normal;
  cursor: pointer;
`;

const ERROR_NOTIFICATION = "Error Notification: Account suspended due to overdue payment";
const CAUTION_NOTIFICATION = "Caution Notification: Payment received, account temporarily restored";

let globalNavigationItems = [];

const Navigation = React.memo(function Navigation(props) {
  const [navigationItems, setNavigationItems] = useState([]);
  const [mobileNavigationItemsIsOpened, setMobileNavigationItemsIsOpened] = useState(false);
  const [bannerStatus, setBannerStatus] = useState({
    notifyWarning: false,
    notifyCaution: false,
  });
  const {
    viewInvoicesPermission,
    managePaymentsPermission,
    viewallotherCasesPermission,
    viewFinancialCasesPermission,
    accountProfilePermission,
    manageyourcontractandservicesPermission,
    userLanguage,
    windowSize,
    contractAccountCountry,
    accountFinancialStatus,
    paymentStatus,
    regionId,
    erpBusinessPartner,
    headerAriaHidden,
    technicianAppointmentValidAccess,
    accountUserTokenDetails,
    isSourcewellGovernment,
  } = useSelector((state) => state.ACCOUNT);
  const { quickBill, contractAccount } = useSelector((state) => state.BILLING);
  const [t, i18n] = useTranslation("leftNavigationTranslation");
  const navigate = useNavigate();
  const location = useLocation();
  const ariaHiddenPath =
    location.pathname === "/account/technicianAppointments/transferDevice" ||
    location.pathname === "/account/technicianAppointments/removeDevice" ||
    location.pathname.includes("/account/technicianAppointments/reschedule/") ||
    headerAriaHidden
      ? "true"
      : "false";

  const isNACOUNTRY = accountUserTokenDetails?.isNA;
  const isAPACCOUNTRY = accountUserTokenDetails?.isAPAC;
  const isEMEACOUNTRY = accountUserTokenDetails?.isEMEA;
  const isNonERPCountryAcc = isNonERPCountryAccount(contractAccountCountry);

  useEffect(() => {
    i18n.changeLanguage(changeLanguageHandler(userLanguage));
  }, [userLanguage]);

  useEffect(() => {
    let isSuspended = null,
      notifyWarning = false;
    if (REDBALL_FEATURE_TOGGLE) {
      // Extract the contractAccountId from props.erpBusinessPartner by matching props.contractAccount
      const selectedContractAccount = erpBusinessPartner?.data?.contractAccountDetails?.find((item) => item.erpCANumber === contractAccount);
      const contractAccountId = selectedContractAccount?.contractAccountId;

      // Get the paymentStatus for the selected contractAccountId from props.paymentStatus
      const selectedPaymentStatus = paymentStatus?.data?.find((item) => item.contractAccountId === contractAccountId)?.paymentStatus;

      isSuspended =
        suspendedFinancialStatuses.includes(accountFinancialStatus?.toLowerCase()) && hasContractAccountDetails(erpBusinessPartner, contractAccount);
      const isInRegion = isAPACCOUNTRY || isNACOUNTRY || isEMEACOUNTRY;
      notifyWarning = isSuspended && selectedPaymentStatus !== "true" && isInRegion;
      setBannerStatus({
        notifyCaution: isSuspended && selectedPaymentStatus === "true" && isInRegion,
        notifyWarning,
      });
    } else {
      isSuspended = false;
    }
    if (isSuspended !== null) {
        navigationItemsHandler(notifyWarning);
    }
  }, [paymentStatus, accountFinancialStatus, regionId, quickBill, contractAccount, erpBusinessPartner, props.billing, contractAccountCountry]);

  
  useEffect(() => {
    if (navigationItems.length > 0 && windowSize.width < DESKTOP) {
      let targetItemIndex = null;
      
      if (location.pathname.includes('/support/cases/')) {
        targetItemIndex = 1; 
      } else if (location.pathname.includes('/account/technicianAppointments/')) {
        targetItemIndex = 3; 
      } else if (location.pathname.includes('/account/billing/')) {
        targetItemIndex = 0;
      } else if (location.pathname.includes('/account/myContract')) {
        targetItemIndex = 2; 
      } else if (location.pathname.includes('/account/myProfile/')) {
        targetItemIndex = 4; 
      }

      if (targetItemIndex !== null) {
        const navItemIndex = globalNavigationItems.findIndex((item) => item?.itemIndex === targetItemIndex);
        if (navItemIndex >= 0) {
          handleMobileNavigationPosition(navItemIndex);
        }
      }
    }
  }, [location.pathname, navigationItems.length, windowSize.width]);

  const navigationItemsHandler = (notifyWarning) => {
    
    let mobileNavigationItem = [];
    //if account is suspended then show only Billing and payment, Contracts and subscriptions and Account profile
    if ((viewInvoicesPermission || managePaymentsPermission) && !isNonERPCountryAcc) {
      mobileNavigationItem.push({ ...renderBillingAndPaymentItem() });
    }
    if (!notifyWarning && (viewallotherCasesPermission || viewFinancialCasesPermission)) {
      mobileNavigationItem.push({ ...renderSupportCasesItem() });
    }
    if (manageyourcontractandservicesPermission && !isNonERPCountryAcc) {
      mobileNavigationItem.push({ ...renderMyContractItem() });
    }
    if (!notifyWarning && technicianAppointmentValidAccess) {
      mobileNavigationItem.push({ ...renderTechnicianAppointmentsItem() });
    }
    if (accountProfilePermission) {
      mobileNavigationItem.push({ ...renderAccountProfileItem() });
    }
      
    setNavigationItems([...mobileNavigationItem]);
    globalNavigationItems = [...mobileNavigationItem];
    mobileNavigationItem.length && getMobileNavigationItemsPosition(props.billing, props.cases, props.myContract, props.technicianAppointments);
  };

  //pull the selected navigation item object
  const getMobileNavigationItemsPosition = (billing, cases, contracts, technicianAppointments) => {
    let mobileNavigationItemsInfo = [...globalNavigationItems],
      navItemIndex = null;
    if (billing === 0) {
      let onlyBillingIndex = mobileNavigationItemsInfo.findIndex((item) => item?.itemIndex === 0);
      navItemIndex = onlyBillingIndex;
    } else if (billing === 2) {
      let onlyAccountProfileIndex = mobileNavigationItemsInfo.findIndex((item) => item?.itemIndex === 4);
      navItemIndex = onlyAccountProfileIndex;
    } else if (cases === 1) {
      let onlyCasesIndex = mobileNavigationItemsInfo.findIndex((item) => item?.itemIndex === 1);
      navItemIndex = onlyCasesIndex;
    } else if (contracts === 3) {
      let onlyContractsAndServices = mobileNavigationItemsInfo.findIndex((item) => item?.itemIndex === 2);
      navItemIndex = onlyContractsAndServices;
    } else if (technicianAppointments === 4) {
      let onlyTechnicianAppointments = mobileNavigationItemsInfo.findIndex((item) => item?.itemIndex === 3);
      navItemIndex = onlyTechnicianAppointments;
    }
    navItemIndex >= 0 && handleMobileNavigationPosition(navItemIndex);
  };

  //swap the navigation items positions and default to selected route/navigation item
  const handleMobileNavigationPosition = (index) => {
    let navItems = [...globalNavigationItems];
    [navItems[0], navItems[index]] = [navItems[index], navItems[0]];

    setNavigationItems([...navItems]);
    globalNavigationItems = [...navItems];
    setMobileNavigationItemsIsOpened(false);
  };

  //Handle selected navigation item click
  const selectItem = (index) => {
    switch (index) {
      case 0:
        navigate("/account/billing/invoice");
        break;
      case 1:
        navigate("/support/cases/caseList");
        break;
      case 2:
        navigate("/account/myContract");
        break;
      case 3:
        navigate("/account/technicianAppointments");
        break;
      case 4:
        navigate("/account/myProfile/primaryAccount");
        break;
      default:
        break;
    }
    const navItemIndex = globalNavigationItems.findIndex((item) => item?.itemIndex === index);
    handleMobileNavigationPosition(navItemIndex);
  };

  const getNotificationMessage = () => {
  if (bannerStatus.notifyWarning) return `, ${t(ERROR_NOTIFICATION)}`;
  if (bannerStatus.notifyCaution) return `, ${t(CAUTION_NOTIFICATION)}`;
  return "";
  };

  const renderBillingAndPaymentItem = () => ({
    index: 0,
    itemIndex: 0,
    label: t("Billing and payment"),
    id: "billing-and-payment",
    ariaLabel: `Billing and payment, View your current bill, invoice and payment history, make payments${getNotificationMessage()}`,
    ariaCurrent: props.billing === 0 ? "page" : false,
    ariaHidden: ariaHiddenPath,
    onClickHandler: () => selectItem(0),
    onKeyDownHandler: (e) => e.key === "Enter" && selectItem(0),
    styles: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      padding: "10px",
      gap: "10px",
      isolation: "isolate",
      width: "auto",
      height: "auto",
      justifyContent: "flex-start",
    },
    content: (notifyWarning, notifyCaution) => (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "10px",
          width: "100%",
        }}
      >
        <div>{t("Billing and payment")}</div>
        {notifyWarning || notifyCaution ? (
          <img
            aria-hidden="false" 
            aria-label={notifyWarning ? t(ERROR_NOTIFICATION) : t(CAUTION_NOTIFICATION)}
            alt={notifyWarning ? t(ERROR_NOTIFICATION) : t(CAUTION_NOTIFICATION)}
            src={notifyWarning ? RedIcon : CautionIcon}
            style={{
              width: "16px",
              height: "16px",
              flex: "none",
              order: 0,
              flexGrow: 0,
              zIndex: 1,
            }}
          />
        ) : (
          <></>
        )}
      </div>
    ),
  });

  const renderSupportCasesItem = () => ({
    index: 1,
    itemIndex: 1,
    label: t("Support cases"),
    id: "support-cases",
    ariaLabel: "Support cases, view all your case details",
    ariaCurrent: props.cases === 1 ? "page" : false,
    ariaHidden: ariaHiddenPath,
    onClickHandler: () => selectItem(1),
    onKeyDownHandler: (e) => e.key === "Enter" && selectItem(1),
  });

  const renderAccountProfileItem = () => ({
    index: 2,
    itemIndex: 4,
    label: t("Account profile"),
    id: "account-profile",
    ariaLabel: "Account profile, view your account details and contact list",
    ariaCurrent: props.billing === 2 ? "page" : false,
    ariaHidden: ariaHiddenPath,
    onClickHandler: () => selectItem(4),
    onKeyDownHandler: (e) => e.key === "Enter" && selectItem(4),
  });

  const renderMyContractItem = () => ({
    index: 3,
    itemIndex: 2,
    label: t("Contracts and subscriptions"),
    id: "my-contract",
    ariaLabel: "Contracts and subscriptions, view your contract details",
    ariaCurrent: props.myContract === 3 ? "page" : false,
    ariaHidden: ariaHiddenPath,
    onClickHandler: () => selectItem(2),
    onKeyDownHandler: (e) => e.key === "Enter" && selectItem(2),
  });

  const renderTechnicianAppointmentsItem = () => ({
    index: 4,
    itemIndex: 3,
    label: t("Technician appointments"),
    id: isSourcewellGovernment ? "technician-appointments-govt-sourcewell" : "technician-appointments",
    ariaLabel: "Technician appointments, view your technician appointments related details",
    ariaCurrent: props.technicianAppointments === 4 ? "page" : false,
    ariaHidden: ariaHiddenPath,
    onClickHandler: () => selectItem(3),
    onKeyDownHandler: (e) => e.key === "Enter" && selectItem(3),
  });

  return (
    <>
      {windowSize.width >= DESKTOP ? (
        <>
          {navigationItems
            ?.toSorted((a, b) => a.itemIndex - b.itemIndex)
            .map((item, index) => (
              <NavigationItem
                key={item.id}
                id={item.id}
                tabIndex="0"
                aria-label={item.ariaLabel}
                aria-current={item.ariaCurrent}
                index={item.index}
                onClick={item.onClickHandler}
                onKeyDown={item.onKeyDownHandler}
                marginTop={index === 0 ? "1.5rem" : "0"}
                style={{ ...item.styles }}
                aria-hidden={headerAriaHidden}
                {...props}
              >
                {item.content?.(bannerStatus.notifyWarning, bannerStatus.notifyCaution) || item.label}
              </NavigationItem>
            ))}
        </>
      ) : (
        <>
          {/* mobile view navigation items */}
          <Accordion id="mobile-navigation">
            <AccordionItem opened={mobileNavigationItemsIsOpened} topLine={false}>
              {navigationItems?.map((item, index) => {
                return (
                  <>
                    {index === 0 ? (
                      <AccordionHeader onClick={() => setMobileNavigationItemsIsOpened(true)} key={item.id}>
                        {item.content?.(bannerStatus.notifyWarning, bannerStatus.notifyCaution) || item.label}
                      </AccordionHeader>
                    ) : (
                      <AccordionDetail>
                        <StyledAccordionSubItems onClick={() => selectItem(item.itemIndex)} key={item.id}>
                          {item.label}
                        </StyledAccordionSubItems>
                      </AccordionDetail>
                    )}
                  </>
                );
              })}
            </AccordionItem>
          </Accordion>
        </>
      )}
    </>
  );
});

Navigation.propTypes = {
  billing: PropTypes.number,
  cases: PropTypes.number,
  myContract: PropTypes.number,
  technicianAppointments: PropTypes.number,
};

export default Navigation;
